import { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Period } from 'components/AnalyticsPage/Periods';
import { Steps } from 'components/AnalyticsPage/Spets';

interface ChartTitleProps {
  title: string;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  showCustom: boolean;
  setShowCustom: (value: boolean) => void;
  showStepFilter?: boolean;
  step?: string;
  setStep?: (value: string) => void;
  period: Period;
  setPeriod: (value: Period) => void;
}

const EncsChartTitle: React.FC<ChartTitleProps> = ({
  title,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  showCustom,
  setShowCustom,
  showStepFilter,
  step,
  setStep,
  period,
  setPeriod,
}) => {
  const { t } = useTranslation();
  const handlePeriodChange = useCallback(
    (newPeriod: Period) => {
      setPeriod(newPeriod);
      if (newPeriod === Period.YEAR) {
        const oneYearAgo = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(oneYearAgo);
        setEndDate(today);
        setShowCustom(false);
      } else if (newPeriod === Period.THREE_MONTH) {
        const threeMonthsAgo = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(threeMonthsAgo);
        setEndDate(today);
        setShowCustom(false);
      } else if (newPeriod === Period.MONTH) {
        const oneMonthAgo = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(oneMonthAgo);
        setEndDate(today);
        setShowCustom(false);
      } else if (newPeriod === Period.CUSTOM) {
        setShowCustom(true);
      }
    },
    [setStartDate, setEndDate, setShowCustom, setPeriod],
  );

  useEffect(() => {
    handlePeriodChange(period);
  }, [period, handlePeriodChange]);

  return (
    <div className="ml-space-between">
      <div className="d-flex">
        <div className="d-flex align-items-center">
          <h4 className="text-truncate m-0">{title}</h4>
        </div>
        {showStepFilter && setStep && (
          <div className="d-flex mx-3">
            <select className="form-select rounded-pill ms-2" value={step} onChange={e => setStep(e.target.value)}>
              <option value={Steps.WEEK}>Weekly</option>
              <option value={Steps.MONTH}>Monthly</option>
              <option value={Steps.DAY}>Daily</option>
            </select>
          </div>
        )}
      </div>

      <div className="d-flex">
        {showCustom && (
          <div className="d-flex align-items-center">
            <div className="m-1">
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="m-1">
              <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>
        )}
        <button
          className="btn btn-ghost-success waves-effect waves-light"
          onClick={() => handlePeriodChange(Period.CUSTOM)}
        >
          {t('ChartsAnalitics.filters.custom')}
        </button>
        <button
          className="btn btn-ghost-success waves-effect waves-light"
          onClick={() => handlePeriodChange(Period.YEAR)}
          disabled={period === 'YEAR'}
        >
          {t('ChartsAnalitics.filters.year')}
        </button>
        <button
          className="btn btn-ghost-success waves-effect waves-light"
          onClick={() => handlePeriodChange(Period.THREE_MONTH)}
          disabled={period === '3MONTH'}
        >
          {t('ChartsAnalitics.filters.3months')}
        </button>
        <button
          className="btn btn-ghost-success waves-effect waves-light"
          onClick={() => handlePeriodChange(Period.MONTH)}
          disabled={period === 'MONTH'}
        >
          {t('ChartsAnalitics.filters.month')}
        </button>
      </div>
    </div>
  );
};

export default EncsChartTitle;
